import { cn } from '@workspace/ui/lib/utils'
import type { ReactNode } from 'react'

export interface ChoiceRowProps {
  /** Small square glyph on the left. */
  icon?: ReactNode
  title: string
  description: ReactNode
  /**
   * The affordance on the right. When `onClick` is set this is rendered as a
   * plain indicator, never as its own control — a button inside a button is
   * invalid markup and swallows the outer click.
   */
  action: ReactNode
  /** Makes the whole row the control. */
  onClick?: () => void
  disabled?: boolean
  className?: string
}

/**
 * One option in a "pick one of these" list: glyph, name, what it does, and its
 * current state on the right.
 *
 * The whole row is the target when `onClick` is given. That is the larger,
 * more forgiving hit area, and it matches what people try first — the right-
 * hand label reads as a status, so making it the only live pixel means most
 * clicks land on nothing.
 */
export function ChoiceRow({
  icon,
  title,
  description,
  action,
  onClick,
  disabled = false,
  className,
}: ChoiceRowProps) {
  const body = (
    <>
      {icon && (
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[14.5px] font-semibold text-foreground">{title}</div>
        <div className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">
          {description}
        </div>
      </div>
      <div className="shrink-0">{action}</div>
    </>
  )

  const shared = cn(
    'flex w-full items-center gap-4 rounded-xl border border-primary/25 bg-card/40 px-4 py-3.5 text-left',
    className
  )

  if (!onClick) return <div className={shared}>{body}</div>

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        shared,
        // A row that responds says so under the pointer. The inert variant
        // above is a plain div, so it keeps the default cursor and reads as
        // what it is: a state, not a control.
        'cursor-pointer transition-colors hover:border-primary/50 hover:bg-card/60',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        'disabled:cursor-not-allowed disabled:opacity-70'
      )}
    >
      {body}
    </button>
  )
}
